import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import WidgetFooter from '../../components/WidgetFooter';
import { toast } from 'sonner';
import { 
  ChevronRight, ChevronLeft, Check, User, 
  Calendar, Send, Loader2, MapPin, Mail,
  Phone, Clock, Video, Building, CreditCard,
  CalendarDays, LayoutGrid, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { widgetApi } from '../../lib/api';
import { getImageUrl } from '../../lib/imageUtils';
import { useEmbedAutoresize } from '../../lib/widgetEmbed';
import UrgencyBadge from '../../components/UrgencyBadge';
import SocialProofBadge from '../../components/SocialProofBadge';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';

const STEPS = [
  { id: 'type', title: 'Type de RDV', icon: Building },
  { id: 'date', title: 'Date & Heure', icon: Calendar },
  { id: 'contact', title: 'Coordonnées', icon: User },
  { id: 'summary', title: 'Confirmation', icon: Check },
];

export default function AppointmentWidget() {
  const { widgetId } = useParams();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const isEmbedded = searchParams.get('embed') === 'true';
  useEmbedAutoresize(isEmbedded);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loadError, setLoadError] = useState(null); // 'not_found' | 'network' | null

  const primaryColor = widgetConfig?.primary_color || '#3B82F6';
  const bgColor = widgetConfig?.bg_color || '#FFFFFF';
  const textColor = widgetConfig?.text_color || '#1a1a1a';

  // View mode: 'month' or 'week'
  const [viewMode, setViewMode] = useState('week');
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    // Start from next Monday if today is weekend, otherwise start from this week's Monday
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 6 ? 2 : (1 - dayOfWeek + 7) % 7);
    const monday = new Date(today);
    monday.setDate(today.getDate() + (dayOfWeek === 1 ? 0 : daysUntilMonday > 0 ? daysUntilMonday : 0));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const [weekAvailability, setWeekAvailability] = useState([]);
  const [loadingWeek, setLoadingWeek] = useState(false);

  const [formData, setFormData] = useState({
    meeting_type: 'physical', // 'physical' or 'online'
    service_id: '',
    selected_date: null,
    selected_time: '',
    collaborator_id: '',
    client_name: searchParams.get('prefill_name') || '',
    client_email: searchParams.get('prefill_email') || '',
    client_phone: searchParams.get('prefill_phone') || '',
    notes: '',
  });

  // Work days (0=Monday, ..., 6=Sunday)
  const [workDays, setWorkDays] = useState([0, 1, 2, 3, 4]); // Default Mon-Fri
  const [closures, setClosures] = useState([]); // Company closures + French holidays

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await widgetApi.getAppointmentConfig(widgetId);
        setConfig(response.data);
        setWidgetConfig(response.data.widget);
        setClosures(response.data.closures || []);
        
        // Extract work days from work_hours if available
        if (response.data.company?.work_hours) {
          const activeDays = response.data.company.work_hours
            .filter(wh => wh.is_active)
            .map(wh => wh.day_of_week);
          if (activeDays.length > 0) {
            setWorkDays(activeDays);
          }
        }
        
        // Auto-select meeting type based on available modes
        const modes = response.data.widget?.appointment_modes || ['presential'];
        if (modes.includes('presential') && !modes.includes('video')) {
          setFormData(prev => ({ ...prev, meeting_type: 'physical' }));
        } else if (modes.includes('video') && !modes.includes('presential')) {
          setFormData(prev => ({ ...prev, meeting_type: 'online' }));
        }
      } catch (error) {
        console.error('Error loading widget config:', error);
        const status = error?.response?.status;
        setLoadError(status === 404 ? 'not_found' : 'network');
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [widgetId]);

  // Load available slots when date is selected (month view)
  useEffect(() => {
    const fetchSlots = async () => {
      if (!formData.selected_date || viewMode !== 'month') return;
      
      setLoadingSlots(true);
      try {
        const dateStr = format(formData.selected_date, 'yyyy-MM-dd');
        const response = await widgetApi.getAppointmentAvailability(widgetId, dateStr, formData.service_id);
        setAvailableSlots(response.data.slots || []);
      } catch (error) {
        console.error('Error loading slots:', error);
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [widgetId, formData.selected_date, formData.service_id, viewMode]);

  // Load week availability (week view)
  useEffect(() => {
    const fetchWeekAvailability = async () => {
      if (viewMode !== 'week' || currentStep !== 1) return;
      
      setLoadingWeek(true);
      try {
        const startDateStr = format(currentWeekStart, 'yyyy-MM-dd');
        const response = await widgetApi.getAppointmentWeekAvailability(widgetId, startDateStr);
        setWeekAvailability(response.data.week || []);
      } catch (error) {
        console.error('Error loading week availability:', error);
        setWeekAvailability([]);
      } finally {
        setLoadingWeek(false);
      }
    };
    fetchWeekAvailability();
  }, [widgetId, currentWeekStart, viewMode, currentStep]);

  const getDaysInMonth = () => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  const getClosureReason = (date) => {
    if (!closures || closures.length === 0) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const iso = `${y}-${m}-${d}`;
    const hit = closures.find((c) => {
      const start = c.start_date;
      const end = c.end_date || start;
      if (!start) return false;
      return iso >= start && iso <= end;
    });
    if (!hit) return null;
    if (hit.type === 'french_holiday') return hit.reason || 'Jour férié';
    return hit.reason || 'Fermeture';
  };

  const isDateAvailable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if date is in the past
    if (!isAfter(date, today)) return false;

    // Block company closures + French holidays
    if (getClosureReason(date)) return false;

    // Convert JS day (0=Sunday, 1=Monday) to our format (0=Monday, 6=Sunday)
    const jsDay = date.getDay();
    const ourDay = jsDay === 0 ? 6 : jsDay - 1; // Convert: Sunday(0)->6, Monday(1)->0, etc.
    
    // Check if this day is a work day
    return workDays.includes(ourDay);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.meeting_type;
      case 1:
        return formData.selected_date && formData.selected_time;
      case 2:
        return formData.client_name && formData.client_email && formData.client_phone;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await widgetApi.createAppointmentBooking(widgetId, {
        meeting_type: formData.meeting_type,
        service_id: formData.service_id,
        date: format(formData.selected_date, 'yyyy-MM-dd'),
        time_slot: formData.selected_time,
        collaborator_id: formData.collaborator_id,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        notes: formData.notes,
      });

      setSubmitted(true);
      toast.success('Demande de RDV envoyée !');
    } catch (error) {
      console.error('Submission error:', error);
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && typeof detail === 'string') {
        // Server detected a stale slot (closure, leave, double-booking)
        toast.error(detail);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Une erreur est survenue');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: primaryColor }} />
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={isEmbedded ? "py-10 px-4" : "min-h-screen flex items-center justify-center p-4"} style={{ backgroundColor: isEmbedded ? 'transparent' : bgColor }}>
        <Card className="max-w-md w-full mx-auto" data-testid="widget-load-error">
          <CardContent className="pt-6 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2 text-slate-900">
              {loadError === 'not_found' ? "Service indisponible" : "Erreur de connexion"}
            </h2>
            <p className="text-gray-600 text-sm">
              {loadError === 'not_found'
                ? "Ce widget de prise de rendez-vous n'est pas accessible pour le moment. Merci de contacter le prestataire."
                : "Impossible de joindre le serveur. Réessayez dans quelques instants."}
            </p>
            {loadError === 'network' && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Réessayer
              </button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
              <Check className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <h2 className="text-2xl font-bold mb-2">{widgetConfig?.confirmation_title || 'Demande envoyée !'}</h2>
            <p className="text-gray-600">
              {widgetConfig?.confirmation_message || `Merci ${formData.client_name} ! Votre demande de RDV a été transmise. Vous recevrez une confirmation par email.`}
            </p>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg text-left">
              <p className="text-sm"><strong>Date :</strong> {format(formData.selected_date, 'EEEE d MMMM yyyy', { locale: fr })}</p>
              <p className="text-sm"><strong>Heure :</strong> {formData.selected_time}</p>
              <p className="text-sm"><strong>Type :</strong> {formData.meeting_type === 'online' ? 'En visio' : 'En présentiel'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className={isEmbedded ? "py-4 px-4" : "min-h-screen py-8 px-4"}
      style={{ backgroundColor: isEmbedded ? 'transparent' : bgColor }}
    >
      <div className="max-w-xl mx-auto">
        {/* Header — hidden when embedded inside client space */}
        {!isEmbedded && (
          <div className="text-center mb-8">
            {config?.company?.logo_url && widgetConfig?.show_logo !== false && (
              <div
                className="w-32 h-32 mx-auto mb-4 rounded-full bg-white flex items-center justify-center overflow-hidden p-3 border-2 transition-all duration-300 hover:scale-105"
                style={{ borderColor: primaryColor, boxShadow: `0 4px 24px ${primaryColor}33` }}
              >
                <img 
                  src={getImageUrl(config.company.logo_url)}
                  alt={config?.company?.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            <h1 className="text-2xl font-bold" style={{ color: textColor }}>{widgetConfig?.name || 'Prendre RDV'}</h1>
            <p className="text-gray-600">{widgetConfig?.description || 'Réservez un créneau avec nous'}</p>
          </div>
        )}

        {/* Steps */}
        <div className="flex items-center justify-center mb-8 gap-2">
          {STEPS.map((step, idx) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  idx <= currentStep ? 'text-white' : 'bg-gray-200 text-gray-500'
                }`}
                style={{ backgroundColor: idx <= currentStep ? primaryColor : undefined }}
              >
                {idx < currentStep ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`w-8 h-1 mx-1 ${idx < currentStep ? '' : 'bg-gray-200'}`} 
                     style={{ backgroundColor: idx < currentStep ? primaryColor : undefined }} />
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>{STEPS[currentStep].title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 0: Meeting Type */}
            {currentStep === 0 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Comment souhaitez-vous effectuer ce rendez-vous ?</p>
                <RadioGroup value={formData.meeting_type} onValueChange={(v) => setFormData(prev => ({ ...prev, meeting_type: v }))}>
                  {(widgetConfig?.appointment_modes || ['presential']).includes('presential') && (
                    <div 
                      className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                        formData.meeting_type === 'physical' ? 'border-2' : 'border-gray-200'
                      }`}
                      style={formData.meeting_type === 'physical' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                      onClick={() => setFormData(prev => ({ ...prev, meeting_type: 'physical' }))}
                    >
                      <RadioGroupItem value="physical" id="physical" />
                      <Building className="w-6 h-6 text-gray-600" />
                      <div>
                        <Label htmlFor="physical" className="font-medium cursor-pointer">En présentiel</Label>
                        <p className="text-sm text-gray-500">Rendez-vous dans nos locaux</p>
                      </div>
                    </div>
                  )}
                  {(widgetConfig?.appointment_modes || []).includes('video') && (
                    <div 
                      className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                        formData.meeting_type === 'online' ? 'border-2' : 'border-gray-200'
                      }`}
                      style={formData.meeting_type === 'online' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                      onClick={() => setFormData(prev => ({ ...prev, meeting_type: 'online' }))}
                    >
                      <RadioGroupItem value="online" id="online" />
                      <Video className="w-6 h-6 text-gray-600" />
                      <div>
                        <Label htmlFor="online" className="font-medium cursor-pointer">En visioconférence</Label>
                        <p className="text-sm text-gray-500">Rendez-vous en ligne (lien envoyé par email)</p>
                      </div>
                    </div>
                  )}
                </RadioGroup>

                {/* Services/Types de RDV selection */}
                {(() => {
                  // Filter services by enabled_services if configured
                  const enabledServices = widgetConfig?.enabled_services || [];
                  const filteredServices = enabledServices.length > 0
                    ? (config?.services || []).filter(s => enabledServices.includes(s.id))
                    : (config?.services || []);
                  
                  const hasAppointmentTypes = widgetConfig?.appointment_service_types?.length > 0;
                  const hasServices = filteredServices.length > 0;
                  
                  if (!hasAppointmentTypes && !hasServices) return null;
                  
                  return (
                    <div className="pt-4 border-t">
                      <Label className="mb-3 block">Type de rendez-vous</Label>
                      <div className="grid grid-cols-1 gap-2">
                        {/* Custom appointment types from widget config */}
                        {widgetConfig?.appointment_service_types?.map(serviceType => (
                          <button
                            key={serviceType.id}
                            onClick={() => setFormData(prev => ({ 
                              ...prev, 
                              service_id: prev.service_id === serviceType.id ? '' : serviceType.id,
                              selected_service_type: prev.service_id === serviceType.id ? null : serviceType
                            }))}
                            className={`p-4 rounded-lg border text-left transition-colors ${
                              formData.service_id === serviceType.id ? 'border-2' : 'border-gray-200'
                            }`}
                            style={formData.service_id === serviceType.id ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="font-medium">{serviceType.name}</span>
                                <span className="text-sm text-gray-500 ml-2">({serviceType.duration} min)</span>
                              </div>
                              <span className={`font-semibold ${serviceType.price > 0 ? 'text-primary' : 'text-green-600'}`}>
                                {serviceType.price > 0 ? `${serviceType.price}€` : 'Gratuit'}
                              </span>
                            </div>
                          </button>
                        ))}
                        
                        {/* Services from company (filtered by enabled_services) */}
                        {hasServices && (
                          <>
                            {hasAppointmentTypes && filteredServices.length > 0 && (
                              <div className="text-sm text-gray-500 mt-2 mb-1 font-medium">Ou choisir un service :</div>
                            )}
                            {filteredServices.map(service => (
                              <button
                                key={service.id}
                                onClick={() => setFormData(prev => ({ 
                                  ...prev, 
                                  service_id: prev.service_id === service.id ? '' : service.id,
                                  selected_service_type: null
                                }))}
                                className={`p-4 rounded-lg border text-left transition-colors ${
                                  formData.service_id === service.id ? 'border-2' : 'border-gray-200'
                                }`}
                                style={formData.service_id === service.id ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <span className="font-medium">{service.name}</span>
                                    {service.duration && (
                                      <span className="text-sm text-gray-500 ml-2">({service.duration} min)</span>
                                    )}
                                  </div>
                                  {service.price > 0 && (
                                    <span className="font-semibold text-primary">{service.price}€</span>
                                  )}
                                </div>
                                {service.description && (
                                  <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                                )}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Step 1: Date & Time */}
            {currentStep === 1 && (
              <div className="space-y-4">
                {/* Real-data social-proof banner */}
                <SocialProofBadge widgetId={widgetId} className="mx-auto w-fit" />

                {/* View mode toggle */}
                <div className="flex items-center justify-center gap-2 p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setViewMode('week')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'week' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                    Semaine
                  </button>
                  <button
                    onClick={() => setViewMode('month')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'month' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <CalendarDays className="w-4 h-4" />
                    Mois
                  </button>
                </div>

                {/* Week View */}
                {viewMode === 'week' && (
                  <div className="space-y-4">
                    {/* Global urgency banner across the visible week */}
                    {!loadingWeek && (() => {
                      const totalAvail = weekAvailability.reduce(
                        (acc, d) => acc + (d.available_slots || 0), 0
                      );
                      const totalSlots = weekAvailability.reduce(
                        (acc, d) => acc + (d.total_slots || 0), 0
                      );
                      // Only show when the week has any slots at all
                      if (totalSlots === 0 || totalAvail === 0) return null;
                      // Tight if ≤ 20% available across the week
                      if (totalAvail > totalSlots * 0.25) return null;
                      return (
                        <UrgencyBadge
                          available={totalAvail}
                          total={totalSlots}
                          className="w-full"
                        />
                      );
                    })()}

                    {/* Week navigation */}
                    <div className="flex items-center justify-between">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setCurrentWeekStart(prev => subWeeks(prev, 1))}
                        disabled={currentWeekStart <= new Date()}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Sem. préc.
                      </Button>
                      <span className="font-medium text-sm">
                        {format(currentWeekStart, 'd MMM', { locale: fr })} - {format(addDays(currentWeekStart, 6), 'd MMM yyyy', { locale: fr })}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setCurrentWeekStart(prev => addWeeks(prev, 1))}
                      >
                        Sem. suiv.
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>

                    {/* Week grid */}
                    {loadingWeek ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: primaryColor }} />
                        <p className="text-sm text-gray-500 mt-2">Chargement des disponibilités...</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <div className="min-w-[600px]">
                          {/* Days header with load indicator */}
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {weekAvailability.map((day, idx) => {
                              const dayDate = new Date(day.date);
                              const isToday = isSameDay(dayDate, new Date());
                              const isPast = dayDate < new Date() && !isToday;
                              const availableCount = day.available_slots || 0;
                              const totalCount = day.total_slots || 0;
                              const loadPercent = day.load_percentage || 0;
                              
                              // Determine load color: green (low), yellow (medium), red (high)
                              const getLoadColor = () => {
                                if (day.is_closed || isPast) return 'bg-gray-200';
                                if (loadPercent >= 80) return 'bg-red-400';
                                if (loadPercent >= 50) return 'bg-amber-400';
                                return 'bg-green-400';
                              };
                              
                              return (
                                <div 
                                  key={idx}
                                  className={`text-center p-2 rounded-t-lg ${
                                    day.is_closed ? 'bg-gray-100 text-gray-400' : 
                                    isPast ? 'bg-gray-50 text-gray-400' :
                                    isToday ? 'bg-blue-50' : 'bg-gray-50'
                                  }`}
                                >
                                  <p className={`text-xs font-medium ${isToday ? 'text-blue-600' : ''}`}>
                                    {day.day_short}
                                  </p>
                                  <p className={`text-lg font-bold ${isToday ? 'text-blue-600' : ''}`}>
                                    {format(new Date(day.date), 'd')}
                                  </p>
                                  {day.is_closed ? (
                                    <span className="text-[10px] text-gray-400">Fermé</span>
                                  ) : !isPast && totalCount > 0 ? (
                                    <div className="mt-1">
                                      <div className="flex items-center justify-center gap-1">
                                        <span className={`text-[10px] font-medium ${
                                          loadPercent >= 80 ? 'text-red-600' : 
                                          loadPercent >= 50 ? 'text-amber-600' : 
                                          'text-green-600'
                                        }`}>
                                          {availableCount}/{totalCount}
                                        </span>
                                      </div>
                                      {/* Load bar */}
                                      <div className="w-full h-1 bg-gray-200 rounded-full mt-1 overflow-hidden">
                                        <div 
                                          className={`h-full ${getLoadColor()} transition-all`}
                                          style={{ width: `${100 - loadPercent}%` }}
                                        />
                                      </div>
                                    </div>
                                  ) : isPast ? (
                                    <span className="text-[10px] text-gray-400">Passé</span>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>

                          {/* Time slots grid */}
                          <div className="grid grid-cols-7 gap-1">
                            {weekAvailability.map((day, dayIdx) => {
                              const dayDate = new Date(day.date);
                              const isPast = dayDate < new Date() && !isSameDay(dayDate, new Date());
                              
                              return (
                                <div key={dayIdx} className="space-y-1">
                                  {day.is_closed || isPast ? (
                                    <div className="h-48 flex items-center justify-center text-gray-300 text-xs">
                                      {isPast ? 'Passé' : 'Fermé'}
                                    </div>
                                  ) : day.slots.length === 0 ? (
                                    <div className="h-48 flex items-center justify-center text-gray-400 text-xs">
                                      Aucun créneau
                                    </div>
                                  ) : (
                                    <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
                                      {day.slots.filter(s => s.available).map((slot, slotIdx) => {
                                        const isSelected = 
                                          formData.selected_date && 
                                          isSameDay(new Date(day.date), formData.selected_date) && 
                                          formData.selected_time === slot.time;
                                        
                                        return (
                                          <button
                                            key={slotIdx}
                                            onClick={() => setFormData(prev => ({
                                              ...prev,
                                              selected_date: new Date(day.date),
                                              selected_time: slot.time,
                                              collaborator_id: slot.collaborator_id
                                            }))}
                                            className={`w-full p-1.5 rounded text-xs font-medium transition-all ${
                                              isSelected 
                                                ? 'text-white shadow-sm scale-105' 
                                                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                                            }`}
                                            style={isSelected ? { backgroundColor: primaryColor } : {}}
                                          >
                                            {slot.time}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Selected slot summary */}
                    {formData.selected_date && formData.selected_time && (
                      <div 
                        className="p-3 rounded-lg border-2 text-center"
                        style={{ borderColor: primaryColor, backgroundColor: `${primaryColor}10` }}
                      >
                        <p className="text-sm font-medium" style={{ color: primaryColor }}>
                          <Check className="w-4 h-4 inline mr-1" />
                          {format(formData.selected_date, 'EEEE d MMMM', { locale: fr })} à {formData.selected_time}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Month View (original) */}
                {viewMode === 'month' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <Button variant="outline" size="icon" onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="font-medium capitalize">
                        {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                      </span>
                      <Button variant="outline" size="icon" onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center text-sm">
                      {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                        <div key={d} className="font-medium py-2 text-gray-500">{d}</div>
                      ))}
                      {getDaysInMonth().map((day, idx) => {
                        const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                        const isAvailable = isDateAvailable(day);
                        const isSelected = formData.selected_date && isSameDay(day, formData.selected_date);
                        const closureReason = getClosureReason(day);

                        return (
                          <button
                            key={idx}
                            onClick={() => isAvailable && isCurrentMonth && setFormData(prev => ({ ...prev, selected_date: day, selected_time: '' }))}
                            disabled={!isAvailable || !isCurrentMonth}
                            title={closureReason || undefined}
                            className={`p-2 rounded-lg text-sm transition-colors flex flex-col items-center gap-0.5 ${
                              !isCurrentMonth ? 'opacity-30' : ''
                            } ${isSelected ? 'text-white' : isAvailable ? 'hover:bg-gray-100' : closureReason ? 'text-amber-700 bg-amber-50' : 'text-gray-300'}`}
                            style={isSelected ? { backgroundColor: primaryColor } : {}}
                          >
                            <span>{format(day, 'd')}</span>
                            {closureReason && (
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Time slots */}
                    {formData.selected_date && (
                      <div className="pt-4 border-t space-y-3">
                        <Label className="block">
                          Créneaux disponibles le {format(formData.selected_date, 'EEEE d MMMM', { locale: fr })}
                        </Label>
                        {!loadingSlots && availableSlots.length > 0 && (
                          <UrgencyBadge available={availableSlots.length} />
                        )}
                        {loadingSlots ? (
                          <div className="text-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                          </div>
                        ) : availableSlots.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">Aucun créneau disponible ce jour</p>
                        ) : (
                          <div className="grid grid-cols-4 gap-2">
                            {availableSlots.map((slot, idx) => (
                              <button
                                key={idx}
                                onClick={() => setFormData(prev => ({ 
                                  ...prev, 
                                  selected_time: slot.time,
                                  collaborator_id: slot.collaborator_id
                                }))}
                                className={`p-2 rounded-lg border text-sm transition-colors ${
                                  formData.selected_time === slot.time ? 'text-white' : 'hover:bg-gray-50'
                                }`}
                                style={formData.selected_time === slot.time ? { backgroundColor: primaryColor } : {}}
                              >
                                {slot.time}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 2: Contact */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom complet *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone *</Label>
                    <Input
                      value={formData.client_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="jean@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message (optionnel)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Précisez l'objet de votre rendez-vous..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Summary */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-3">Récapitulatif de votre demande</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      {formData.meeting_type === 'online' ? <Video className="w-4 h-4" /> : <Building className="w-4 h-4" />}
                      <span>{formData.meeting_type === 'online' ? 'Visioconférence' : 'En présentiel'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{format(formData.selected_date, 'EEEE d MMMM yyyy', { locale: fr })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{formData.selected_time}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-3">Vos coordonnées</h3>
                  <div className="space-y-1 text-sm">
                    <p><User className="w-4 h-4 inline mr-2" />{formData.client_name}</p>
                    <p><Mail className="w-4 h-4 inline mr-2" />{formData.client_email}</p>
                    <p><Phone className="w-4 h-4 inline mr-2" />{formData.client_phone}</p>
                  </div>
                </div>

                {config?.allow_payment && config?.require_payment && (
                  <div className="p-4 rounded-lg border-2 border-dashed" style={{ borderColor: primaryColor }}>
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="w-5 h-5" style={{ color: primaryColor }} />
                      <span className="font-medium">Paiement requis</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Le paiement sera demandé après confirmation de votre RDV.
                    </p>
                  </div>
                )}

                <p className="text-sm text-gray-500 text-center">
                  En cliquant sur "Envoyer", vous recevrez une confirmation par email une fois le RDV accepté.
                </p>

                {/* CGV Checkbox */}
                {widgetConfig?.terms_enabled && widgetConfig?.terms_link && (
                  <div className="flex items-start gap-3 p-4 rounded-lg border" style={{ borderColor: termsAccepted ? primaryColor : '#e5e7eb' }}>
                    <input
                      type="checkbox"
                      id="terms-acceptance"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-4 h-4 cursor-pointer"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms-acceptance" className="cursor-pointer text-sm leading-relaxed">
                      {widgetConfig.terms_text || "J'accepte les conditions générales de vente"}{' '}
                      <a 
                        href={widgetConfig.terms_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline font-medium"
                        style={{ color: primaryColor }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Lire les CGV
                      </a>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              {currentStep > 0 ? (
                <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)}>
                  <ChevronLeft className="w-4 h-4 mr-2" /> Retour
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < STEPS.length - 1 ? (
                <Button
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  disabled={!canProceed()}
                  style={{ backgroundColor: primaryColor }}
                >
                  Suivant <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || (widgetConfig?.terms_enabled && widgetConfig?.terms_link && !termsAccepted)}
                  style={{ backgroundColor: primaryColor }}
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> {widgetConfig?.submit_button_text || 'Envoyer'}</>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer - Propulsé par ResaWork + Widget ID (hidden for Premium subscribers) */}
        <WidgetFooter widgetId={widgetId} isPremium={config?.is_premium} />
      </div>
    </div>
  );
}
